import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadMultipleImages } from '../../../utility/firebaseStorageHelpers';
import {
  insertDocument,
  updateDocument,
  updateSiteMetrics,
} from '../../../utility/firebaseHelpers';
import { serverTimestamp, deleteField } from 'firebase/firestore';
import { generateNumericPropertyId } from '../../../utility/property-utilities/generateNumericPropertyId';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../../auth/firebaseConfig';
import { Listing } from '../../../components/property/property-card/PropertyCardTypes';

/**
 * Manages listing submission logic (create, update, save as draft)
 * Encapsulates form submission, AI content checks, and navigation
 *
 * Image Upload Flow (Busboy/FormData pattern):
 * 1. Frontend collects images in FileList
 * 2. uploadMultipleImages() creates FormData with files
 * 3. FormData sent to /uploadImages endpoint (propertyId in query)
 * 4. Backend Busboy parses multipart/form-data stream
 * 5. Each file compressed with Sharp (rotate → colorspace → resize → mozjpeg)
 * 6. Compressed images uploaded to Firebase Storage
 * 7. Array of { url, path, filename, fileSize } returned to frontend
 * 8. URLs stored in Firestore listing document
 */
export const useListingSubmission = (currentUser: any, userProfile: any, draftListing: any) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSaveAsDraft = async (
    data: any,
    locationInput: string,
    listingIntent: any,
    existingDraftId: number | null,
    storedImages: FileList | null,
    prefilledImages: string[],
    setError: any
  ) => {
    if (!currentUser) return;
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (!data.berRating || data.berRating.trim() === '') {
        setError('berRating', {
          type: 'manual',
          message: 'BER rating is required',
        });
        setSubmitError('Please select a BER rating before saving your draft.');
        setIsSubmitting(false);
        return;
      }

      if (!userProfile) {
        setSubmitError('Unable to save draft. User profile not loaded.');
        setIsSubmitting(false);
        return;
      }
      const propertyId = existingDraftId || generateNumericPropertyId(14);
      const isUpdatingDraft = !!existingDraftId;

      let imageUrls: string[] = [...prefilledImages];

      if (storedImages && storedImages.length > 0) {
        const newImageUrls = await uploadMultipleImages(
          storedImages,
          `listings/${propertyId}`,
          true
        );
        imageUrls = [...imageUrls, ...newImageUrls];
      } else if (draftListing?.images && imageUrls.length === 0) {
        imageUrls = draftListing.images;
      }

      const expirationDate = new Date();
      expirationDate.setMonth(expirationDate.getMonth() + 3);

      const listingData: Listing = {
        id: propertyId,
        title: data.title || 'Untitled Draft',
        type: data.type || 'Apartment',
        price: Number(data.price) || 0,
        location: locationInput.toLowerCase() || '',
        bedrooms: data.bedrooms || 1,
        bathrooms: data.bathrooms || 1,
        size: data.size,
        status: 'draft',
        virtualTourUrl: data.virtualTourUrl,
        listingIntent: listingIntent,
        propertyOwnerUid: currentUser?.uid ?? '',
        description: data.description || '',
        amenities: data.amenities || [],
        availableFrom: data.availableFrom || new Date(),
        expirationDate: expirationDate,
        berRating: data.berRating,
        images: imageUrls,
        videoUrl: data.videoUrl,
        user: userProfile,
        dateAdded:
          isUpdatingDraft && draftListing?.dateAdded ? draftListing.dateAdded : serverTimestamp(),
        propertyViews:
          isUpdatingDraft && draftListing?.propertyViews ? draftListing.propertyViews : 0,
        isExternalListing: false,
        draftReminderCount:
          isUpdatingDraft && draftListing?.draftReminderCount !== undefined
            ? draftListing.draftReminderCount
            : 0,
        ...(data.accessibility && { accessibility: data.accessibility }),
      };

      if (isUpdatingDraft && draftListing?.lastReminderSent) {
        listingData.lastReminderSent = draftListing.lastReminderSent;
      }

      if (isUpdatingDraft) {
        await updateDocument<Listing>('listings', propertyId.toString(), listingData);
      } else {
        await insertDocument<Listing>('listings', listingData, propertyId.toString());
      }

      navigate('/my-listings', {
        state: { draftSaved: true },
      });
    } catch (error) {
      console.error('Error saving draft:', error);
      setSubmitError('Failed to save draft. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (
    data: any,
    locationInput: string,
    listingIntent: any,
    existingDraftId: number | null,
    storedImages: FileList | null,
    prefilledImages: string[],
    setError: any
  ) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // AI-powered discrimination check
      if (data.description) {
        try {
          const combinedDescription = `${data.title} ${data.description}`;
          const discriminationResponse = await fetch(
            `${CLOUD_FUNCTIONS_BASE_URL}/detectDiscriminatoryContent`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ text: combinedDescription }),
            }
          );

          if (!discriminationResponse.ok) {
            throw new Error('Discrimination check failed');
          }

          const analysis = await discriminationResponse.json();
          if (analysis.isDiscriminatory) {
            setError('description', {
              type: 'manual',
              message: `Potentially discriminatory language found: ${analysis.reason}. Please revise.`,
            });
            setSubmitError(
              `After reviewing your listing description, we noticed some potential issues: ${analysis.reason}. Please revise it to continue.`
            );
            setIsSubmitting(false);
            return;
          }
        } catch (e) {
          console.error('Error calling content analysis endpoint:', e);
        }
      }

      if (!data.berRating || data.berRating.trim() === '') {
        setError('berRating', {
          type: 'manual',
          message: 'BER rating is required',
        });
        setSubmitError('Please select a BER rating to continue.');
        setIsSubmitting(false);
        return;
      }

      // Unauthenticated user: save to localStorage
      if (!currentUser) {
        const listingDraft = {
          title: data.title,
          type: data.type,
          price: Number(data.price),
          location: locationInput.toLowerCase(),
          bedrooms: data.bedrooms,
          bathrooms: data.bathrooms,
          size: data.size,
          virtualTourUrl: data.virtualTourUrl,
          listingIntent: listingIntent,
          description: data.description,
          amenities: data.amenities,
          availableFrom: data.availableFrom.toISOString(),
          berRating: data.berRating,
          videoUrl: data.videoUrl,
          timestamp: new Date().toISOString(),
        };

        localStorage.setItem('pendingListing', JSON.stringify(listingDraft));

        if (storedImages) {
          const imagePromises = Array.from(storedImages).map((file) => {
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(file);
            });
          });

          const imageData = await Promise.all(imagePromises);
          localStorage.setItem('pendingListingImages', JSON.stringify(imageData));
        }

        navigate('/signup?from=create-listing');
        return;
      }

      // Authenticated user: create/update listing
      if (userProfile && (storedImages || prefilledImages.length > 0)) {
        const propertyId = existingDraftId || generateNumericPropertyId(14);
        const isUpdatingDraft = !!existingDraftId;

        let imageUrls: string[] = [...prefilledImages];

        if (storedImages && storedImages.length > 0) {
          const newImageUrls = await uploadMultipleImages(
            storedImages,
            `listings/${propertyId}`,
            true
          );
          imageUrls = [...imageUrls, ...newImageUrls];
        } else if (draftListing?.images && imageUrls.length === 0) {
          imageUrls = draftListing.images;
        }

        if (imageUrls.length === 0) {
          throw new Error('At least one image is required');
        }

        const expirationDate = new Date();
        expirationDate.setMonth(expirationDate.getMonth() + 3);

        const listingData: Listing = {
          id: propertyId,
          title: data.title,
          type: data.type,
          price: Number(data.price),
          location: locationInput.toLowerCase(),
          bedrooms: data.bedrooms,
          bathrooms: data.bathrooms,
          size: data.size,
          status: 'active',
          virtualTourUrl: data.virtualTourUrl,
          listingIntent: listingIntent,
          propertyOwnerUid: currentUser?.uid ?? '',
          description: data.description,
          amenities: data.amenities,
          availableFrom: data.availableFrom,
          expirationDate: expirationDate,
          berRating: data.berRating,
          images: imageUrls,
          videoUrl: data.videoUrl,
          user: userProfile,
          dateAdded:
            isUpdatingDraft && draftListing?.dateAdded ? draftListing.dateAdded : serverTimestamp(),
          propertyViews:
            isUpdatingDraft && draftListing?.propertyViews ? draftListing.propertyViews : 0,
          isExternalListing: false,
          ...(data.accessibility && { accessibility: data.accessibility }),
        };

        if (isUpdatingDraft) {
          listingData.draftReminderCount = deleteField() as any;
          listingData.lastReminderSent = deleteField() as any;
        }

        data.propertyId = propertyId;
        data.location = locationInput;

        if (isUpdatingDraft) {
          await updateDocument<Listing>('listings', propertyId.toString(), listingData);

          if (draftListing?.status !== 'active') {
            await updateSiteMetrics({ activeProperties: 1 });
          }
        } else {
          await insertDocument<Listing>('listings', listingData, propertyId.toString());
          await updateSiteMetrics({ activeProperties: 1 });
        }

        navigate('/listing-success', {
          state: {
            listingSummary: data,
          },
        });
      }
    } catch (error) {
      console.error('Error creating listing:', error);
      setSubmitError('Failed to create listing. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    submitError,
    setSubmitError,
    handleSaveAsDraft,
    handleSubmit,
  };
};

export default useListingSubmission;
